import T from "i18n-react/dist/i18n-react";
import Swal from "sweetalert2";
import * as Sentry from "@sentry/react";

export const buildSwalPayload = (title, content, type) => ({ title, html: content, type });
export const buildSwalErrorPayload = (title, content) => buildSwalPayload(title, content, "error");
export const buildSwalWarningPayload = (title, content) => buildSwalPayload(title, content, "warning");

const isClientError = (error) => {
  const statusCode = error?.res?.statusCode;
  return statusCode >= 400 && statusCode < 500;
};
const isServerError = (error) => error?.res?.statusCode >= 500;
const isNetworkError = (error) => error?.err instanceof Error && !error?.res;
// For callers that already got the dialog from alertNetworkOrServerErrorAndRethrow and
// must not present a request failure as a "not found" result.
export const isNetworkOrServerError = (error) => isNetworkError(error) || isServerError(error);

// API requests carry the OAuth token as an access_token query parameter, so any URL
// that reaches Sentry has to go through this first.
export const stripAccessToken = (url) => {
  if (!url) return undefined;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete("access_token");
    return parsed.toString();
  } catch (e) {
    return url.replace(/([?&])access_token=[^&#]*/gi, "$1access_token=[filtered]");
  }
};

// Sentry.init beforeBreadcrumb hook: XHR/fetch breadcrumbs record the full request URL
// (data.url); navigation breadcrumbs record the page URL with its query (data.from/to).
export const scrubBreadcrumb = (breadcrumb) => {
  const data = breadcrumb?.data;
  if (data) {
    ["url", "from", "to"].forEach((key) => {
      if (typeof data[key] === "string") data[key] = stripAccessToken(data[key]);
    });
  }
  return breadcrumb;
};

// For arbitrary strings (serialized objects, exception messages) where the value is not
// necessarily a URL: mask the token in place instead of re-parsing the string as a URL.
// Covers the query-string form (access_token=...) and the JSON form ("access_token":"...").
const TOKEN_IN_STRING = /(^|[?&\s"'])access_token=[^&#\s"']*/gi;
const TOKEN_IN_JSON = /("(?:access_token|accessTokenQS|id_token|refresh_token)"\s*:\s*")[^"]*"/gi;
export const maskAccessToken = (value) =>
  value.replace(TOKEN_IN_STRING, "$1access_token=[filtered]").replace(TOKEN_IN_JSON, "$1[filtered]\"");

// Keys whose value is a credential no matter what it looks like: the redux state that
// gets serialized into extras holds the kiosk token under baseState.accessTokenQS.
const SENSITIVE_KEY = /token|secret|password|authorization/i;

const MAX_SCRUB_DEPTH = 8;
const scrubDeep = (value, depth = 0) => {
  if (typeof value === "string") return maskAccessToken(value);
  if (value === null || typeof value !== "object" || depth >= MAX_SCRUB_DEPTH) return value;
  if (Array.isArray(value)) {
    value.forEach((item, i) => { value[i] = scrubDeep(item, depth + 1); });
  } else {
    Object.keys(value).forEach((key) => {
      const current = value[key];
      if (SENSITIVE_KEY.test(key) && current !== null && current !== undefined && typeof current !== "object") {
        value[key] = "[filtered]";
      } else {
        value[key] = scrubDeep(current, depth + 1);
      }
    });
  }
  return value;
};

// Sentry.init beforeSend hook: the page URL can carry the token too (?access_token=),
// breadcrumbs attached to the event are scrubbed again in case any bypassed the
// breadcrumb hook, and anything serialized into extras or exception values (an
// unhandled { err, res } rejection carries err.url, for instance) is masked.
export const scrubSentryEvent = (event) => {
  if (event?.request?.url) {
    event.request.url = stripAccessToken(event.request.url);
  }
  if (Array.isArray(event?.breadcrumbs)) {
    event.breadcrumbs.forEach(scrubBreadcrumb);
  }
  if (event?.extra) {
    scrubDeep(event.extra);
  }
  if (Array.isArray(event?.exception?.values)) {
    event.exception.values.forEach((exception) => {
      if (typeof exception?.value === "string") exception.value = maskAccessToken(exception.value);
    });
  }
  if (typeof event?.message === "string") {
    event.message = maskAccessToken(event.message);
  }
  return event;
};

// Sentry.init beforeSendTransaction hook: http.client spans carry the request URL in
// their description and data.url. Tracing is currently not sampled in this app, but the
// hook keeps the token out of performance data the day it is.
export const scrubSentryTransaction = (event) => {
  scrubSentryEvent(event);
  if (Array.isArray(event?.spans)) {
    event.spans.forEach((span) => {
      if (typeof span?.description === "string") span.description = stripAccessToken(span.description);
      if (typeof span?.data?.url === "string") span.data.url = stripAccessToken(span.data.url);
    });
  }
  return event;
};

// Replay beforeAddRecordingEvent hook. Recording events do not go through beforeSend,
// and the SDK only hands Custom events (performanceSpan, breadcrumb) to this callback.
// The performanceSpan built from the PerformanceNavigationTiming entry carries the page
// URL as loaded, with ?access_token= when the app was opened that way, because that
// entry exists before AuthorizedRoute strips the token from the address bar.
// (The rrweb Meta event's href is not reachable from here; it is clean because the route
// replaces the URL before Replay's first snapshot, which starts on a setTimeout.)
const REPLAY_EVENT_CUSTOM = 5; // rrweb EventType.Custom
export const scrubReplayEvent = (event) => {
  if (event?.type === REPLAY_EVENT_CUSTOM && event.data?.tag === "performanceSpan") {
    const payload = event.data.payload;
    if (typeof payload?.description === "string") payload.description = stripAccessToken(payload.description);
  }
  return event;
};

// Path of a request URL with its variable segments collapsed, for grouping: ticket ids,
// summit ids and base64 QR codes become "#", so one endpoint is one Sentry issue.
export const normalizePath = (url) => {
  if (!url) return "unknown";
  let pathname;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch (e) {
    pathname = url.split(/[?#]/)[0];
  }
  return pathname
    .split("/")
    .map((segment) => (/^\d+$/.test(segment) || segment.length >= 20 ? "#" : segment))
    .join("/");
};

// Network errors (no response at all) and server errors are shown to the operator
// in a dialog, so they never reach Sentry on their own. Report them explicitly with
// the request context we have, so onsite "Network Error" dialogs can be traced.
const reportToSentry = (error, kind) => {
  const err = error?.err;
  const statusCode = error?.res?.statusCode;
  // superagent sets method/url on the error only for crossDomain (no response) errors;
  // for HTTP status errors the request lives on the response.
  const req = error?.res?.req;
  const method = err?.method || req?.method;
  const url = err?.url || req?.url;
  Sentry.withScope((scope) => {
    // Every superagent crossDomain error shares the same message and stack, so without
    // a fingerprint the SDK's Dedupe integration drops consecutive failures on different
    // endpoints, and over HTTP/2 (empty statusText) 5xx titles would be the raw response
    // body. Group by kind + method + normalized path instead.
    scope.setFingerprint([kind, method || "unknown", normalizePath(url)]);
    scope.setTag("error.kind", kind);
    scope.setTag("http.method", method || "unknown");
    scope.setTag("http.status", statusCode ? String(statusCode) : "none");
    scope.setExtras({
      url: stripAccessToken(url),
      timeout: Boolean(err?.timeout),
      crossDomain: Boolean(err?.crossDomain),
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    });
    Sentry.captureException(err instanceof Error ? err : new Error(`${kind} without error object`));
  });
};

const getClientErrorMessage = (error) => {
  const code = error?.res?.statusCode;
  if (code === 404) {
    const errorKey = error.res.body?.message;
    return T.texts.errors[errorKey] || T.translate(`errors.${code}`);
  } else if (code === 412) {
    const errors = error.res.body?.errors || [];
    let message = "";
    console.log(errors)
    for (const [key, value] of Object.entries(errors)) {
      message += isNaN(key) ? T.texts.errors[key] : "";
      message += `${T.texts.errors[value]}<br/>`;
    }
    return message;
  } else {
    return T.translate(`errors.${code}`) || T.translate("errors.default");
  }
};

const getClientErrorTitle = (error) => {
  const code = error?.res?.statusCode;
  return T.translate(`errors.${code}_title`) || T.translate("errors.default_title");
};

export const alertError = (error) => {
  let title, message;

  if (isClientError(error)) {
    title = getClientErrorTitle(error);
    message = getClientErrorMessage(error);
  } else if (isServerError(error)) {
    reportToSentry(error, "server_error");
    title = T.translate("errors.server_error_title");
    message = T.translate("errors.server_error");
  } else if (isNetworkError(error)) {
    reportToSentry(error, "network_error");
    title = T.translate("errors.network_error_title");
    message = error.message || T.translate("errors.network_error");
  } else {
    console.error("An unexpected error occurred: ", error);
    return;
  }

  const swalPayload = buildSwalErrorPayload(title, message);
  Swal.fire(swalPayload);
};

export const alertErrorAndRethrow = (error) => {
  alertError(error);
  throw error;
};

export const alertNetworkOrServerErrorAndRethrow = (error) => {
  if (isNetworkError(error) || isServerError(error)) {
    alertError(error);
  }
  throw error;
};

export const handleAuthAlert = (payload, callback = null) => (dispatch) => {
  const hasCallback = callback && typeof callback === "function";
  if (payload.httpCode === 401 || payload.httpCode === 403) {
    Swal.fire(payload).then(result => hasCallback && callback());
  }
};

export const withAlertHandler = (authErrorHandler, alertHandler) => (error, response) => (dispatch) => 
  authErrorHandler(error, response, alertHandler)(dispatch);
